/**
 * Page d'administration des backups - Interface SIMPLE et CLAIRE
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const BackupsAdmin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [sejours, setSejours] = useState([]);
  const [lastBackupDate, setLastBackupDate] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [creating, setCreating] = useState(false);

  // Dialog pour créer un nouveau backup
  const [showDialog, setShowDialog] = useState(false);
  const [backupType, setBackupType] = useState('COMPLET');
  const [selectedSejour, setSelectedSejour] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadBackups(),
        loadSejours(),
        loadLastBackupDate()
      ]);
    } catch (err) {
      logger.error('Erreur lors du chargement:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadBackups = async () => {
    const response = await api.get('/logs/backups/');
    const data = response.data.results || response.data;
    setBackups(Array.isArray(data) ? data : []);
  };

  const loadSejours = async () => {
    const response = await api.get('/sejours/');
    const data = response.data.results || response.data;
    setSejours(Array.isArray(data) ? data : []);
  };

  const loadLastBackupDate = async () => {
    const response = await api.get('/logs/backups/dernier_backup/');
    setLastBackupDate(response.data.date);
  };

  const handleCreateBackup = async () => {
    if (backupType === 'SEJOUR' && !selectedSejour) {
      setError('Veuillez sélectionner un séjour');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const data = {
        type_backup: backupType,
        description: description,
      };

      if (backupType === 'SEJOUR') {
        data.sejour = selectedSejour;
      }

      // Créer le backup
      const response = await api.post('/logs/backups/', data);
      const newBackup = response.data;

      setSuccess('Backup créé avec succès ! Téléchargement en cours...');
      setShowDialog(false);
      setBackupType('COMPLET');
      setSelectedSejour('');
      setDescription('');

      // Télécharger automatiquement le backup créé
      if (newBackup.id && newBackup.statut === 'COMPLETE') {
        handleDownloadBackup(newBackup.id);
      }

      // Recharger les données
      loadData();
    } catch (err) {
      logger.error('Erreur lors de la création du backup:', err);
      setError(err.response?.data?.error || 'Erreur lors de la création du backup');
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadBackup = (backupId) => {
    // Nginx sert le ZIP directement depuis le disque via X-Accel-Redirect.
    // Le navigateur gère le téléchargement nativement : le fichier ne transite
    // jamais par la mémoire JavaScript, ce qui permet les fichiers de plusieurs centaines de Mo.
    // Le cookie JWT httpOnly est envoyé automatiquement par le navigateur (même domaine).
    const link = document.createElement('a');
    link.href = `/api/logs/backups/${backupId}/telecharger/`;
    link.setAttribute('download', '');
    document.body.appendChild(link);
    link.click();
    link.remove();
    setSuccess('Téléchargement démarré');
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce backup ?')) {
      return;
    }

    try {
      await api.delete(`/logs/backups/${backupId}/`);
      setSuccess('Backup supprimé avec succès');
      loadData();
    } catch (err) {
      logger.error('Erreur lors de la suppression:', err);
      setError('Erreur lors de la suppression du backup');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'COMPLETE':
        return 'bg-green-100 text-green-800';
      case 'EN_COURS':
        return 'bg-yellow-100 text-yellow-800';
      case 'ERREUR':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center text-xl">Chargement des backups...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">💾 Gestion des Backups</h1>
          <div className="flex gap-3">
            <button
              onClick={loadData}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium"
            >
              🔄 Rafraîchir
            </button>
            <button
              onClick={() => setShowDialog(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
            >
              ➕ Créer un backup
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button
              onClick={() => setError(null)}
              className="float-right text-xl font-bold"
            >
              ×
            </button>
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
            <button
              onClick={() => setSuccess(null)}
              className="float-right text-xl font-bold"
            >
              ×
            </button>
          </div>
        )}

        {/* Cartes d'information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded border border-blue-200">
            <h3 className="text-sm font-semibold text-blue-800 mb-1">
              Dernier backup complet
            </h3>
            <p className="text-2xl font-bold text-blue-900">
              {formatDate(lastBackupDate)}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded border border-green-200">
            <h3 className="text-sm font-semibold text-green-800 mb-1">
              Total de backups
            </h3>
            <p className="text-2xl font-bold text-green-900">
              {backups.length}
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
          <p className="text-sm text-blue-800">
            <strong>ℹ️ Information :</strong> Les backups incluent toutes les inscriptions, documents,
            paiements, informations personnelles et fichiers associés. En cas de problème avec la base
            de données, vous pourrez facilement récupérer les données et continuer les processus
            d'inscriptions.
          </p>
        </div>
      </div>

      {/* Liste des backups */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">📋 Historique des backups</h2>

          {backups.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <p className="text-xl mb-2">Aucun backup disponible</p>
              <p className="text-sm">Créez votre premier backup pour sécuriser vos données</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Séjour
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date de création
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Taille
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Statistiques
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Créé par
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {backups.map((backup) => (
                    <tr key={backup.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            backup.type_backup === 'COMPLET'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {backup.type_backup_display}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {backup.sejour_titre || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(backup.date_creation)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatutColor(backup.statut)}`}>
                          {backup.statut_display}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {backup.taille_lisible}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        <div>{backup.nb_inscriptions} inscriptions</div>
                        <div>{backup.nb_documents} documents</div>
                        <div>{backup.nb_paiements} paiements</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {backup.created_by_nom || 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          {backup.statut === 'COMPLETE' && (
                            <button
                              onClick={() => handleDownloadBackup(backup.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Télécharger"
                            >
                              ⬇️
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteBackup(backup.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Dialog pour créer un backup */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Créer un nouveau backup</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de backup
                  </label>
                  <select
                    value={backupType}
                    onChange={(e) => setBackupType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="COMPLET">Backup complet (tout le site)</option>
                    <option value="SEJOUR">Backup par séjour</option>
                  </select>
                </div>

                {backupType === 'SEJOUR' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Séjour
                    </label>
                    <select
                      value={selectedSejour}
                      onChange={(e) => setSelectedSejour(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sélectionner un séjour</option>
                      {sejours.map((sejour) => (
                        <option key={sejour.id} value={sejour.id}>
                          {sejour.titre} ({new Date(sejour.date_debut).toLocaleDateString('fr-FR')} - {new Date(sejour.date_fin).toLocaleDateString('fr-FR')})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (optionnelle)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    placeholder="Ajouter une description pour ce backup..."
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="text-sm text-blue-800">
                    Le backup inclura toutes les données d'inscriptions, documents, paiements,
                    informations personnelles, et fichiers associés.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowDialog(false);
                    setBackupType('COMPLET');
                    setSelectedSejour('');
                    setDescription('');
                  }}
                  disabled={creating}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateBackup}
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
                >
                  {creating ? '⏳ Création en cours...' : '✅ Créer le backup'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupsAdmin;
